/**
 * Data patch for a polled generation job that has ended without a result.
 *
 * A job-polling handler marks the node `jobStatus: 'processing'` while the job
 * runs. Ending it by resetting only `isProcessing` left `jobStatus` behind, and
 * canvas undo (src/utils/flowHistory.ts `isNodeRunning`) then treated the node
 * as generating forever — "Can't undo while a node is generating" on a node
 * that failed long ago. Every end state must go through this.
 */
export type EndedJobStatus = 'failed' | 'timeout';

/**
 * Nodes whose queued job is still being polled after the generation handler
 * returned (Wan Animate) or after a reload (resumed Veo jobs). This is the
 * authoritative "in flight" signal for those paths — the handler's
 * GenerationLifecycleService run has already finished by then.
 */
const activePollers = new Map<string, string>();

export function markJobPolling(nodeId: string, jobId: string): void {
  activePollers.set(nodeId, jobId);
}

export function endJobPolling(nodeId: string, jobId?: string): void {
  if (jobId === undefined || activePollers.get(nodeId) === jobId) activePollers.delete(nodeId);
}

export function isJobPolling(nodeId: string): boolean {
  return activePollers.has(nodeId);
}

export function endedJobPatch(status: EndedJobStatus, error?: string | null): {
  isProcessing: false;
  jobStatus: EndedJobStatus;
  error: string | null;
} {
  return { isProcessing: false, jobStatus: status, error: error ?? null };
}
